import { memo } from "react";
import { getDimensionSet, getTireType, getTireManufacturers } from "services/api/fleet/tires/helpers";
import { transformDateToLocalDateString } from "helpers";
import { FleetTireSetEditFormValues } from "services/api/fleet/tires/types";
import { RelocateFormValues } from "../types";

interface RelocateSummaryProps {
  currentSet: FleetTireSetEditFormValues;
  formValues: RelocateFormValues;
  resolvedNames: {
    targetWarehouse?: { name?: string | null; address?: string | null };
  };
}

const InfoRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex justify-between items-start py-2.5">
    <span className="text-sm text-slate-500 min-w-[160px]">{label}</span>
    <div className="text-sm font-medium text-slate-900 text-right">{children}</div>
  </div>
);

const RelocateSummary: React.FC<RelocateSummaryProps> = ({ currentSet, formValues, resolvedNames }) => {
  const { targetWarehouse } = resolvedNames;
  const tireType = getTireType(currentSet.tires);
  const dimension = getDimensionSet(currentSet.tires);
  const manufacturers = getTireManufacturers(currentSet.tires);
  const dateStr = formValues.transferDate ? (transformDateToLocalDateString(formValues.transferDate) ?? "-") : "-";

  return (
    <div className="flex items-center justify-center px-4 min-h-[70vh] 2xl:min-h-[1000px]">
      <div className="w-full max-w-2xl">
        <h1 className="text-2xl font-bold text-slate-900 text-center mb-1 mb-2">Potvrda premještaja</h1>
        <p className="text-center text-md text-slate-500 mb-8">Provjerite podatke prije izvršenja premještaja.</p>

        <div className="space-y-4">
          {/* Set guma koji se premješta */}
          <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
            <div className="px-5 pt-4 pb-1">
              <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                Set guma koji se premješta
              </div>
              <div className="divide-y divide-slate-100">
                <InfoRow label="Kod seta"><span className="font-semibold">{currentSet.code}</span></InfoRow>
                <InfoRow label="Dimenzija">{dimension || "-"}</InfoRow>
                <InfoRow label="Tip gume">{tireType?.name ?? "-"}</InfoRow>
                <InfoRow label="Proizvođač">{manufacturers || "-"}</InfoRow>
                <InfoRow label="Broj guma">{currentSet.tires?.length ?? 0}</InfoRow>
                <InfoRow label="Datum premještaja">{dateStr}</InfoRow>
              </div>
            </div>
          </div>

          {/* Premještaj između skladišta */}
          <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
            <div className="px-5 pt-4 pb-1">
              <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                Premještaj između skladišta
              </div>
              <div className="divide-y divide-slate-100">
                <InfoRow label="Sa skladišta">
                  <span className="font-semibold">{currentSet.warehouse?.name ?? "-"}</span>
                </InfoRow>
                <InfoRow label="Na skladište">
                  <span className="font-semibold">{targetWarehouse?.name ?? "-"}</span>
                </InfoRow>
                {targetWarehouse?.address && (
                  <InfoRow label="Adresa novog skladišta">
                    <span className="text-slate-500">{targetWarehouse.address}</span>
                  </InfoRow>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(RelocateSummary);
